import CloseButton from '@Components/Common/CloseButton';
import DeliveryAddressModal from '@Components/ProductDetailPanel/BuyFlow/CheckoutCore/Modals/DeliveryAddressModal';
import PaymentOptionsModal from '@Components/ProductDetailPanel/BuyFlow/CheckoutCore/Modals/PaymentOptionsModal';
import ProductOptionModal from '@Components/ProductDetailPanel/BuyFlow/CheckoutCore/Modals/ProductOptionModal';
import ProductDetailPanel from '@Components/ProductDetailPanel/ProductDetailPanel';
import { useHamburgerButtonPresenter } from '@Components/SideDrawerRight/HamburgerButton/HamburgerButton.presenter';
import {
  CLOSE_ICON_SIZE,
  DISTANCE_FROM_EDGE,
  DRAWER_WIDTH,
} from '@Constants/LeftDrawer';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import OwnableTheme from '@Theme';
import { utilStyles } from '@Utils/utilStyles';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import styled from 'styled-components/native';

import { useSideDrawerLeftPresenter } from './SideDrawerLeftPresenter';

const StyledContentView = styled.ScrollView`
  box-shadow: 0 0 25px ${OwnableTheme.colors.modalShadow};
  width: 100%;
  height: 100%;
  background-color: white;
  border-radius: 15px;
`;

const CloseButtonWrapper = styled.View`
  position: absolute;
  top: 0;
  z-index: 200;
`;

const SideDrawerLeft = () => {
  const { isOpen, close } = useSideDrawerLeftPresenter();
  const { isOpen: isOpenRight } = useHamburgerButtonPresenter();

  useEffect(() => {
    if (isOpen) {
      isOpenRight.set(false);
    }
  }, [isOpen]);
  const {
    productOptionsModalVisible,
    deliveryAddressModalVisible,
    paymentOptionsModalVisible,
    scrollViewManager,
  } = useCheckoutPresenter();

  const { width: windowWidth } = useWindowDimensions();
  const width = DRAWER_WIDTH(windowWidth);

  const toValue = isOpen
    ? 0
    : -(width + CLOSE_ICON_SIZE + 2 * DISTANCE_FROM_EDGE);

  const leftOffsetRef = useRef(new Animated.Value(toValue));

  useEffect(() => {
    Animated.timing(leftOffsetRef.current, {
      toValue,
      duration: 500,
      easing: Easing.out(Easing.ease),
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [isOpen]);

  // on screen resize do not animate, just resize instantly
  useEffect(() => {
    leftOffsetRef.current.setValue(toValue);
  }, [width]);

  const animated = { transform: [{ translateX: leftOffsetRef.current }] };

  const closeButtonPosition = (): { top: number; right: number } => {
    if (windowWidth >= 875) {
      return { right: -42, top: 12 };
    }

    if (windowWidth >= 800) {
      return { right: 24, top: 12 };
    }

    return { right: 24, top: 62 };
  };

  const closeButtonSize = windowWidth >= 800 ? 'medium' : 'large';

  return (
    <Animated.View style={[styles.wrapper, { width }, animated]}>
      <CloseButtonWrapper style={{ ...closeButtonPosition() }}>
        <CloseButton
          onPress={close}
          bgColor={OwnableTheme.colors.black}
          iconColor={OwnableTheme.colors.white}
          size={closeButtonSize}
          iconSize={CLOSE_ICON_SIZE}
        />
      </CloseButtonWrapper>
      <StyledContentView ref={scrollViewManager.scrollViewRef}>
        <View
          style={utilStyles.alignItemsEnd}
          ref={scrollViewManager.parentRef}
        >
          <ProductDetailPanel />
        </View>
      </StyledContentView>
      {productOptionsModalVisible.value && <ProductOptionModal />}
      {deliveryAddressModalVisible.value && <DeliveryAddressModal />}
      {paymentOptionsModalVisible.value && <PaymentOptionsModal />}
    </Animated.View>
  );
};

export default observer(SideDrawerLeft);

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: DISTANCE_FROM_EDGE,
    left: DISTANCE_FROM_EDGE,
    bottom: DISTANCE_FROM_EDGE,
  },
});
